import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { apiUrl } from './apiBase.js';

interface RuntimeConfig {
  entraTenantId: string;
  entraClientId: string;
  env: string;
  version: string;
}

interface User {
  name: string;
  email?: string;
  /** For demo: a synthetic tenantId so demo data doesn't collide. */
  tenantId: string;
  oid: string;
  demo: boolean;
}

type AuthState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated'; config: RuntimeConfig | null }
  | { kind: 'authenticated'; user: User; config: RuntimeConfig | null }
  | { kind: 'helper-done' };

interface AuthApi {
  state: AuthState;
  signIn(): void;
  signInDemo(): void;
  signOut(): void;
  /** Returns a fresh ID token for the active MS account, or null in demo / unauthed.
   *  Pass forceRefresh=true to bypass MSAL's cache and demand a freshly-minted
   *  token from Entra (used by the API client to retry a 401). */
  getIdToken(opts?: { forceRefresh?: boolean }): Promise<string | null>;
  /**
   * Acquire an access token for the given scopes (e.g. Microsoft Graph).
   * Returns null in demo / unauthed mode, or when interaction is required —
   * the caller should degrade gracefully.
   */
  getAccessToken(scopes: string[], opts?: { forceRefresh?: boolean }): Promise<string | null>;
}

const AuthCtx = createContext<AuthApi | null>(null);

const DEMO_KEY = 'cc.demo-user';
const DEMO_TENANT = 'demo-tenant-00000000-0000-0000-0000-000000000000';
const DEMO_OID = 'demo-user-00000000-0000-0000-0000-000000000000';
const SCOPES = ['openid', 'profile', 'email'];
// When set, a freshly opened tab auto-triggers loginRedirect on boot.
// Used by the side-panel iframe so a one-click "Sign in" in the panel
// kicks off a real top-level auth flow in a new tab.
const AUTO_SIGNIN_PARAM = 'autoSignIn';
// Per-tab marker that survives the redirect round-trip. Lets the tab
// recognise itself as a helper after returning from
// login.microsoftonline.com so it can render the "you can close this"
// page instead of the full SPA.
const HELPER_TAB_KEY = 'cc.helperTab';
// Cross-tab signal channel. Any tab that completes a sign-in or
// sign-out posts here; the side-panel iframe (and any other open tab
// of this SPA) reloads when it receives the message.
const AUTH_CHANNEL = 'cc-auth';

/**
 * MSAL's redirect flow can't work in an iframe (login.microsoftonline.com
 * sets X-Frame-Options: DENY) and the popup flow's window-handshake is
 * unreliable when the opener is itself iframed (Chrome side panel).
 *
 * Strategy when framed: delegate sign-in to a real top-level tab. The
 * iframe never tries to do MSAL itself — it opens a new tab pointing
 * at the SPA, the new tab does loginRedirect normally, and the iframe
 * picks up the result via BroadcastChannel + a localStorage fallback.
 * Sign-in stays a one-click action because the new tab carries
 * ?autoSignIn=1 and auto-triggers the redirect on boot.
 */
const IS_FRAMED = (() => {
  try {
    return window.top !== window.self;
  } catch {
    // Cross-origin parent — definitely framed.
    return true;
  }
})();

function userFromAccount(account: AccountInfo): User {
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  return {
    name: account.name ?? account.username,
    email: account.username,
    tenantId: typeof claims.tid === 'string' ? claims.tid : account.tenantId,
    oid: typeof claims.oid === 'string' ? claims.oid : account.localAccountId,
    demo: false
  };
}

function broadcastAuth(type: 'signed-in' | 'signed-out') {
  try {
    const ch = new BroadcastChannel(AUTH_CHANNEL);
    ch.postMessage({ type });
    ch.close();
  } catch {
    // BroadcastChannel unsupported — listeners fall back to storage events.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });
  const [msal, setMsal] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const demo = sessionStorage.getItem(DEMO_KEY);

      let config: RuntimeConfig | null = null;
      try {
        const res = await fetch(apiUrl('/api/config'));
        if (res.ok) config = (await res.json()) as RuntimeConfig;
      } catch {
        config = null;
      }
      if (cancelled) return;

      if (demo) {
        setState({
          kind: 'authenticated',
          user: { name: demo, tenantId: DEMO_TENANT, oid: DEMO_OID, demo: true },
          config
        });
        return;
      }

      if (!config?.entraClientId || !config?.entraTenantId) {
        setState({ kind: 'unauthenticated', config });
        return;
      }

      const pca = new PublicClientApplication({
        auth: {
          clientId: config.entraClientId,
          authority: `https://login.microsoftonline.com/${config.entraTenantId}`,
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin
        },
        // localStorage so a sign-in done in a regular tab is visible to
        // the side-panel iframe (and any other tab on the same origin),
        // instead of being trapped in sessionStorage.
        cache: { cacheLocation: 'localStorage' }
      });

      // ?autoSignIn=1 marks a tab opened by the iframe to perform
      // sign-in. Mirror that into sessionStorage so the marker
      // survives the round-trip to login.microsoftonline.com and
      // back, and strip the query param so MSAL's redirect URI
      // matching (which only knows the plain origin) isn't tripped up.
      const params = new URLSearchParams(window.location.search);
      if (params.get(AUTO_SIGNIN_PARAM) === '1') {
        sessionStorage.setItem(HELPER_TAB_KEY, '1');
        history.replaceState(
          {},
          '',
          window.location.pathname + window.location.hash
        );
      }
      const isHelperTab = sessionStorage.getItem(HELPER_TAB_KEY) === '1';

      let signedInThisLoad = false;
      try {
        await pca.initialize();
        const result = await pca.handleRedirectPromise();
        if (result?.account) {
          pca.setActiveAccount(result.account);
          signedInThisLoad = true;
        } else {
          const accounts = pca.getAllAccounts();
          if (accounts.length > 0) pca.setActiveAccount(accounts[0]!);
        }
      } catch (err) {
        console.error('MSAL init failed', err);
      }
      if (cancelled) return;

      // Helper tab not yet signed in → kick off loginRedirect.
      if (isHelperTab && !pca.getActiveAccount()) {
        try {
          await pca.loginRedirect({ scopes: SCOPES });
        } catch (err) {
          console.error('autoSignIn loginRedirect failed', err);
        }
        return;
      }

      // Helper tab is now signed in → tell the iframe to refresh and
      // render the "you can close this" page instead of the full SPA.
      // Auto-close is best-effort; some Chrome versions block close
      // for tabs that have been navigated cross-origin and back.
      if (isHelperTab && pca.getActiveAccount()) {
        sessionStorage.removeItem(HELPER_TAB_KEY);
        broadcastAuth('signed-in');
        setState({ kind: 'helper-done' });
        return;
      }

      // Tell any other open tab/iframe of the SPA that we just signed
      // in. Cheap to fire even if no one's listening.
      if (signedInThisLoad) broadcastAuth('signed-in');

      setMsal(pca);
      const active = pca.getActiveAccount();
      if (active) {
        setState({ kind: 'authenticated', user: userFromAccount(active), config });
      } else {
        setState({ kind: 'unauthenticated', config });
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  // When framed, reload on auth-state changes from another tab. The
  // BroadcastChannel is the primary signal; the storage listener is a
  // fallback for browsers / contexts where BroadcastChannel doesn't
  // propagate. Reload (rather than re-deriving state in place)
  // sidesteps MSAL's internal cache layout — the next init() naturally
  // restores the active account from localStorage.
  useEffect(() => {
    if (!IS_FRAMED) return;
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(AUTH_CHANNEL);
      ch.onmessage = (e: MessageEvent) => {
        const t = (e.data as { type?: string } | null)?.type;
        if (t === 'signed-in' || t === 'signed-out') {
          window.location.reload();
        }
      };
    } catch {
      // ignore — fall back to storage listener
    }
    function onStorage(e: StorageEvent) {
      // msal.account.keys is the canonical signal that an account was
      // added or removed. Storage event with key=null means
      // localStorage.clear() was called (logout in some MSAL versions).
      if (e.key === 'msal.account.keys' || e.key === null) {
        window.location.reload();
      }
    }
    window.addEventListener('storage', onStorage);
    return () => {
      ch?.close();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const api = useMemo<AuthApi>(
    () => ({
      state,
      signIn() {
        if (!msal) return;
        if (IS_FRAMED) {
          // Open a regular tab. autoSignIn auto-triggers the redirect
          // there so the user only clicks Sign in once. The new tab
          // stays open after auth as a normal SPA tab; the iframe
          // reloads via BroadcastChannel.
          window.open(
            `${window.location.origin}/?${AUTO_SIGNIN_PARAM}=1`,
            '_blank'
          );
          return;
        }
        void msal.loginRedirect({ scopes: SCOPES });
      },
      signInDemo() {
        const name = 'Demo User';
        sessionStorage.setItem(DEMO_KEY, name);
        setState((s) => ({
          kind: 'authenticated',
          user: { name, tenantId: DEMO_TENANT, oid: DEMO_OID, demo: true },
          config:
            s.kind === 'loading' || s.kind === 'helper-done' ? null : s.config
        }));
      },
      signOut() {
        const wasDemo = state.kind === 'authenticated' && state.user.demo;
        sessionStorage.removeItem(DEMO_KEY);
        if (wasDemo || !msal) {
          setState((s) => ({
            kind: 'unauthenticated',
            config:
            s.kind === 'loading' || s.kind === 'helper-done' ? null : s.config
          }));
          return;
        }
        if (IS_FRAMED) {
          // Local logout only — clearCache wipes MSAL's localStorage,
          // broadcast tells any other tab to reload. logoutRedirect
          // would need a top-level nav we can't perform from inside
          // the iframe.
          void msal.clearCache();
          broadcastAuth('signed-out');
          setState((s) => ({
            kind: 'unauthenticated',
            config:
            s.kind === 'loading' || s.kind === 'helper-done' ? null : s.config
          }));
          return;
        }
        broadcastAuth('signed-out');
        void msal.logoutRedirect();
      },
      async getIdToken(opts) {
        if (!msal) return null;
        const account = msal.getActiveAccount();
        if (!account) return null;
        try {
          const result = await msal.acquireTokenSilent({
            scopes: SCOPES,
            account,
            forceRefresh: opts?.forceRefresh
          });
          return result.idToken ?? null;
        } catch (err) {
          console.error('acquireTokenSilent failed', err);
          return null;
        }
      },
      async getAccessToken(scopes, opts) {
        if (!msal) return null;
        const account = msal.getActiveAccount();
        if (!account) return null;
        try {
          const result = await msal.acquireTokenSilent({
            scopes,
            account,
            forceRefresh: opts?.forceRefresh
          });
          return result.accessToken ?? null;
        } catch (err) {
          console.warn('acquireTokenSilent (access) failed', err);
          return null;
        }
      }
    }),
    [state, msal]
  );

  return <AuthCtx.Provider value={api}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export const DEMO_TENANT_ID = DEMO_TENANT;
export const DEMO_USER_OID = DEMO_OID;
